import React, { useState, useCallback, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import {
  Timer,
  ChecklistRtl,
  AccessTime,
  SelfImprovement,
} from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';

import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { useActiveSession } from '../../hooks/useActiveSession';
import { usePomodoro } from '../../hooks/usePomodoro';

import PageShell from '../../components/shared/PageShell';
import SurfaceCard from '../../components/shared/SurfaceCard';
import PillTabs from '../../components/shared/PillTabs';
import SessionTicker from '../../components/shared/SessionTicker';
import CoinEarnedAnimation from '../../components/coins/CoinEarnedAnimation';
import PomodoroTimer from '../../components/focus/PomodoroTimer';
import TodoList from '../../components/focus/TodoList';
import SessionClock from '../../components/focus/SessionClock';

import { pageVariants, pageTransition } from '../../theme/motion';
import { RADIUS } from '../../theme/tokens';

const MotionBox = motion(Box);

const TABS = [
  { key: 'timer', label: 'Pomodoro', icon: <Timer fontSize="small" /> },
  { key: 'todo',  label: 'Todo',    icon: <ChecklistRtl fontSize="small" /> },
  { key: 'clock', label: 'Clock',   icon: <AccessTime fontSize="small" /> },
];

/**
 * FocusMode — immersive study companion.
 * Pomodoro state lives in usePomodoro (persisted, background-safe).
 * Active session consumed from useActiveSession (shared hook).
 */
function FocusMode() {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  const { session, loading: sessionLoading } = useActiveSession();

  const [activeTab, setActiveTab] = useState('timer');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [coinAnimation, setCoinAnimation] = useState({ show: false, rewards: [], total: 0 });

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Award coins when a work block completes (same endpoint / payload as original)
  const handlePomodoroComplete = useCallback(async (mode) => {
    if (mode !== 'work') return;

    try {
      const response = await api.post('/coins/pomodoro');
      const coins = response.data.data;
      if (coins?.earned > 0) {
        setCoinAnimation({
          show: true,
          rewards: coins.rewards?.map((r) => ({ amount: r.amount, description: r.reason })) ?? [],
          total: coins.earned,
        });
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        showSnackbar('Check in to earn coins', 'info');
      } else {
        // Optimistic skip — don't block the user's session
        showSnackbar("Couldn't record coins, try again later", 'warning');
      }
    }
  }, [showSnackbar]);

  const pomodoro = usePomodoro({ onComplete: handlePomodoroComplete });

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  return (
    <PageShell title="Focus mode" backTo={-1} maxWidth={false}>
      {/* Session ticker chip or check-in nudge — SurfaceCard directly under the AppBar */}
      <SurfaceCard radius="lg" elevation="sm" sx={{ mx: 2, mt: 1, mb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {!sessionLoading && session ? (
            <Chip
              icon={<SelfImprovement sx={{ fontSize: 16 }} />}
              label={<SessionTicker enteredAt={session.entered_at} />}
              size="small"
              sx={{ bgcolor: 'action.selected', fontWeight: 600 }}
            />
          ) : (
            <Typography
              component={RouterLink}
              to="/student/scanner"
              variant="caption"
              color="text.secondary"
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Check in to track your session toward today's limit
            </Typography>
          )}
        </Box>
      </SurfaceCard>

      {/* Content column */}
      <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, pb: 6 }}>
        {/* Pomodoro dial — always visible */}
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <PomodoroTimer
            mode={pomodoro.mode}
            remainingMs={pomodoro.remainingMs}
            durationMs={pomodoro.durationMs}
            isRunning={pomodoro.isRunning}
            cycleCount={pomodoro.cycleCount}
            start={pomodoro.start}
            pause={pomodoro.pause}
            reset={pomodoro.reset}
            skip={pomodoro.skip}
          />
        </Box>

        {/* Tab switcher */}
        <Box display="flex" justifyContent="center" mb={3}>
          <PillTabs
            tabs={TABS}
            value={activeTab}
            onChange={setActiveTab}
            variant="outline"
          />
        </Box>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          <MotionBox
            key={activeTab}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
          >
            {activeTab === 'timer' && (
              <Box>
                <Typography variant="body2" align="center" color="text.secondary">
                  {pomodoro.isRunning ? 'Timer is running' : 'Press play to start'}
                </Typography>
                {pomodoro.totalWorkSecondsToday > 0 && (
                  <Typography variant="caption" display="block" align="center" color="text.secondary" sx={{ mt: 0.5 }}>
                    {Math.floor(pomodoro.totalWorkSecondsToday / 60)} min focused today
                  </Typography>
                )}
              </Box>
            )}

            {activeTab === 'todo' && (
              <TodoList showSnackbar={showSnackbar} />
            )}

            {activeTab === 'clock' && (
              <SessionClock session={session} />
            )}
          </MotionBox>
        </AnimatePresence>
      </Box>

      {/* Coin earned animation */}
      <CoinEarnedAnimation
        show={coinAnimation.show}
        rewards={coinAnimation.rewards}
        totalEarned={coinAnimation.total}
        onClose={() => setCoinAnimation((prev) => ({ ...prev, show: false }))}
      />

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: RADIUS.md }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
}

export default FocusMode;
